import SidebarPath, { SIDEBAR_COLLAPSED, SIDEBAR_MENU_ITEMS_VISIBLE, SIDEBAR_TABLET_EXPANDED } from './constants';

export const PENDING_ACTION_COUNTS_REFRESH_EVENT = 'pending-action-counts:refresh';
const PENDING_ACTION_COUNT_MUTATION_PATHS = [
  /^\/timesheets\/\d+\/update_status$/,
  /^\/timesheets\/\d+\/update_denial_reason$/,
  /^\/organizations\/\d+\/timesheets\/update_status_multiple$/,
  /^\/organizations\/\d+\/timesheets\/update_status_multiple_wired$/,
  /^\/organizations\/\d+\/manual_time_requests\/\d+\/deny$/,
  /^\/organizations\/\d+\/manual_time_requests\/batch_deny$/,
  /^\/organizations\/\d+\/time_entries\/batch_approve_manual_time_requests$/,
  /^\/time_entries\/approve_manual_time_request$/,
];

export const dispatchPendingActionCountsRefresh = () => {
  window.dispatchEvent(new CustomEvent(PENDING_ACTION_COUNTS_REFRESH_EVENT));
};

export const shouldRefreshPendingActionCounts = (url) => {
  if (!url) {
    return false;
  }

  const pathname = url instanceof URL ? url.pathname : new URL(url, window.location.origin).pathname;
  // Turbo confirmation forms post to `.turbo_stream` URLs, so normalize them before matching.
  const normalizedPathname = pathname.replace(/\.turbo_stream$/, '');

  return PENDING_ACTION_COUNT_MUTATION_PATHS.some((pattern) => pattern.test(normalizedPathname));
};

const PENDING_ACTION_SCOPE = 'menu';
const PENDING_ACTION_KEYS = ['timesheets', 'edit_approve', 'manual_time_requests', 'view_edit', 'approvals'];

const pluralize = (count, singular) => (count === 1 ? singular : `${singular}s`);

const pendingActionCountLabel = (count) => (count > 99 ? '99+' : String(count));

const setCookie = (name, value) => {
  const expDate = new Date().setDate(new Date().getDate() + 90);

  document.cookie = `${name}=${value}; path=/; expires=${new Date(expDate).toUTCString()}`;
};
const getCookie = (name) => {
  const parts = `; ${document.cookie}`.split(`; ${name}=`);

  return parts.length === 2 ? parts.pop().split(';').shift() : null;
};

export default function hubstaffSidebarInitializer(Alpine) {
  Alpine.data('sidebar', ({ alwaysOpenedMenus, initialCollapsed }) => {
    const initialCollapsedState = initialCollapsed !== undefined ? initialCollapsed : (getCookie(SIDEBAR_COLLAPSED) === '1');
    const initialMenuItemsVisible = getCookie(SIDEBAR_MENU_ITEMS_VISIBLE) === '1';
    const state = Alpine.reactive({
      visible: true,
      collapsed: initialCollapsedState,
      // Hack to help us recalculate the nested menus style correctly while triggering a force render
      _forceMenuItemsRender: 0,
      openedMenus: ['favorites'],
      openedCollapsedMenu: null,
      currentPath: `${window.location.pathname}${window.location.search || ''}`,
      alwaysOpenedMenus: [],
      slideAnimationEnabled: false,
      isRendered: false,
      favoriteMenus: [],
      scrollPosition: 0,
      menuItemsVisible: initialMenuItemsVisible,
    });

    return {
      state,

      init() {
        state.alwaysOpenedMenus = alwaysOpenedMenus;
        this.$el.__x = this; // to be accessible from the tests
        this.$watch('state.collapsed', this.onCollapsedChange.bind(this));
        this.$watch('state.visible', (visible) => {
          this.$dispatch('sidebar-toggle', { visible });
          document.body.classList[visible ? 'add' : 'remove']('full-sidebar');
        });

        this.$nextTick(() => {
          this.resize();
          this.onCollapsedChange(state.collapsed);
          state.isRendered = true;
          state._forceMenuItemsRender += 1;
          this.loadPendingActionCounts();
        });

        this.pendingActionCountsRefreshListener = () => {
          this.loadPendingActionCounts();
        };
        this.settingsUpdatedListener = (event) => {
          const settingName = event?.detail?.name;

          if (settingName === 'timesheet_pending_action_notifications_enabled') {
            this.loadPendingActionCounts();
          }

          if (['pending_action_notifications_enabled', 'timesheet_pending_action_notifications_enabled'].includes(settingName)) {
            window.dispatchEvent(new CustomEvent('sidebar-cache:refresh'));
          }
        };

        window.addEventListener(PENDING_ACTION_COUNTS_REFRESH_EVENT, this.pendingActionCountsRefreshListener);
        window.addEventListener('app.settings_updated', this.settingsUpdatedListener);

        this.disableAnimation = Alpine.debounce(() => {
          state.slideAnimationEnabled = false;
        }, 300);

        document
          .querySelector('button.js-mobile-sidebar-toggle')
          ?.addEventListener('click', this.toggleMobileSidebar.bind(this));

        document.addEventListener('turbo:before-stream-render', () => {
          document.querySelectorAll('[role="tooltip"]').forEach((el) => el.remove());
        });
      },

      resize() {
        if (window.innerWidth < 768) {
          // tablet
          state.collapsed = false;
          if (state.visible) {
            state.visible = false;
          }

          return;
        }

        state.visible = true;
        // Collapse sidebar only when window resizes to tablet/mobile
        if (this.state?.isRendered) {
          if (window.innerWidth < 960) {
            state.collapsed = true;
          } else {
            state.collapsed = getCookie(SIDEBAR_COLLAPSED) === '1';
          }
        }
      },

      onCollapsedChange(collapsed) {
        this.$el.addEventListener(
          'transitionend',
          () => {
            state._forceMenuItemsRender += 1;
          },
          { once: true },
        );

        state.openedCollapsedMenu = null;
        document.body.classList[collapsed ? 'add' : 'remove']('sidebar-collapsed');
        document.body.classList[collapsed ? 'remove' : 'add']('tablet-sidebar-expanded');
        this.$dispatch('sidebar-collapse', { collapsed });
      },

      toggleCollapse(collapsed) {
        setCookie(SIDEBAR_TABLET_EXPANDED, collapsed ? 0 : 1);
        setCookie(SIDEBAR_COLLAPSED, collapsed ? 1 : 0);
        state.collapsed = collapsed;
      },

      toggleCollapsedMenu(event, menuId) {
        if (!state.collapsed) {
          // continue to the menu link url
          return true;
        }
        state.openedCollapsedMenu = state.openedCollapsedMenu === menuId ? null : menuId;
        event.preventDefault();
      },

      closeOpenedMenus() {
        state.openedCollapsedMenu = null;
      },

      destroy() {
        window.removeEventListener(PENDING_ACTION_COUNTS_REFRESH_EVENT, this.pendingActionCountsRefreshListener);
        window.removeEventListener('app.settings_updated', this.settingsUpdatedListener);
      },

      toggleMenuItems() {
        state.menuItemsVisible = !state.menuItemsVisible;

        if (window.analytics) {
          const eventName = `Onboarding ${state.menuItemsVisible ? 'show' : 'hide'} sidebar menu items`;

          window.analytics.track(eventName);
        }

        setCookie(SIDEBAR_MENU_ITEMS_VISIBLE, state.menuItemsVisible ? 1 : 0);

        if (state.menuItemsVisible) {
          this.$nextTick(() => {
            state._forceMenuItemsRender += 1;
          });
        }
      },

      isOpenedCollapsedMenu(menuId) {
        return state.openedCollapsedMenu === menuId;
      },

      isPendingActionBadge(target) {
        return !!target?.closest?.('[data-pending-action-key]');
      },

      // flagCheck is a temporary argument to properly distinguish between custom and legacy reports
      isActiveItem(itemPath, parentId, parentPath, flagCheck = false, options = {}) {
        const activePaths = options.activePaths || [];
        const parsedPath = this.formattedPath.replace('ID', state.currentPath.match(/\d+/)?.at(0));
        const paths = [state.currentPath, parsedPath, parsedPath.split('?')[0]];
        // ParsedPath query strings
        const currentPathQuery = new URLSearchParams(parsedPath.split('?')[1]);
        // Sidebar Item information
        const itemWithoutQuery = itemPath.split('?')[0];
        const originalItemWithoutQuery = itemWithoutQuery.replace('custom_', '');
        const itemQuery = new URLSearchParams(itemPath.split('?')[1]);

        // Favorites-section items are duplicated with a `utm_source=favorites` tracking param
        // (see Sidebar#duplicate_item_with_utm) that never appears on the real page URL — it must
        // not participate in the active-item query comparison below.
        itemQuery.delete('utm_source');

        const matchesActivePath = activePaths.some((activePath) => {
          const activePathWithoutQuery = activePath.split('?')[0];

          return (
            paths.includes(activePath) ||
            paths.includes(activePathWithoutQuery) ||
            SidebarPath.matchesPath(parsedPath, activePath) ||
            SidebarPath.matchesPath(parsedPath, activePathWithoutQuery)
          );
        });

        // Mark active "All reports" menu for all pages under path "/reports/ID/report_name"
        if (/^\/reports\/\d+$/.test(itemPath) && !this.skipAllReportsMenu && /^\/reports\/\d+\/[^/]+/.test(parsedPath)) {
          paths.push(originalItemWithoutQuery);
        }

        // Some routes like /teams/:team_id/members have no org ID, so parsedPath gets the wrong number substituted.
        // Compare path structure with all numeric IDs stripped to still match correctly.
        const normalizedParsed = SidebarPath.stripNumericId(parsedPath);
        const normalizedItem = SidebarPath.stripNumericId(itemWithoutQuery);

        if (!paths.includes(originalItemWithoutQuery) && !SidebarPath.matchesPath(parsedPath, itemPath) && !matchesActivePath && normalizedParsed !== normalizedItem) {
          if (parentId) {
            return false;
          }

          const itemWithoutQueryElementCount = itemWithoutQuery.split('/').length;

          return state.currentPath.split('/').slice(0, itemWithoutQueryElementCount).join('/') === itemWithoutQuery;
        }

        // If any report menu was found, then disable all reports menu
        if (/^\/reports\/\d+\/[^/]+/.test(itemPath)) {
          this.skipAllReportsMenu = true;
        }

        if (parentId) {
          const parentQuery =
            parentPath && parentPath.split('?')[1] ? new URLSearchParams(parentPath.split('?')[1]) : null;

          // Compare query string parameters, if both have same key and value doesn't match - return false
          for (const [key, value] of itemQuery.entries()) {
            const hasKey = currentPathQuery.has(key);

            // If current path is missing a required query param, assume parent default subnav is selected
            if (!hasKey && parentQuery) {
              const isActive = parentQuery.get(key) === value;

              if (isActive && !this.state.isRendered) {
                state.openedMenus = [...state.openedMenus, parentId];
              }

              return isActive;
            }
            if (currentPathQuery.get(key) !== value) {
              return false;
            }
          }
        }

        if (!this.state.isRendered) {
          state.openedMenus = [...state.openedMenus, parentId];
        }

        // Handle custom reports and legacy reports with the same name.
        // Applies both under the Reports submenu and under Favorites, since a report can be
        // duplicated into Favorites and still needs the same id/custom disambiguation there.
        if (parentId === 'reports' || parentId === 'favorites') {
          // Check for custom reports that have been favorited (matched by their own report id)
          if (itemQuery.has('id') && flagCheck) {
            return itemQuery.get('id') === currentPathQuery.get('id');
          }

          // Check for the custom report (path contains 'custom_' or item has ?custom query param),
          // only when the current report has no distinguishing id of its own (e.g. legacy report)
          if ((itemPath.includes('custom_') || itemQuery.has('custom')) && flagCheck) {
            return currentPathQuery.has('custom') && !currentPathQuery.has('id');
          }

          return !currentPathQuery.has('custom') && paths.includes(originalItemWithoutQuery);
        }

        return true;
      },

      get favoriteMenusCount() {
        return ` (${state.favoriteMenus.length})`;
      },

      get formattedPath() {
        const parsedPath = state.currentPath.replace(/\/\d+($|\/)/g, '/ID$1').replace(/\/(new|team|me|my)$/, '');
        const expressions = [
          /(dashboard\/\d+)\/team/,
          /(reports\/\d+)\/team/,
          /(\/\d+\/time_entries)\/(daily|weekly|calendar)/,
          /(\/organizations\/\d+\/members)\/\d+/,
        ];
        const prefix = Object.keys(SidebarPath.pathDict.startsWith).find((prefix) => parsedPath.startsWith(prefix) || SidebarPath.matchesPath(prefix, state.currentPath.split('?')[0]));
        const pathExpression = expressions.find((expression) => state.currentPath.match(expression));
        const orgsPath = SidebarPath.normalizePath(parsedPath, 'organizations');

        if (prefix) {
          return SidebarPath.pathDict.startsWith[prefix];
        }

        if (pathExpression) {
          return state.currentPath.replace(pathExpression, '$1');
        }

        if (orgsPath !== parsedPath) {
          return orgsPath;
        }

        if (parsedPath.includes('reports')) {
          return parsedPath.replace(/\/(team|me|my)/, '');
        }

        const isSettingPath = ['settings', 'time_off_policies/archived', 'work_break_policies'].find((item) =>
          parsedPath.includes(item),
        );

        if (isSettingPath) {
          return SidebarPath.normalizePath(parsedPath, 'settings');
        }

        return parsedPath;
      },

      isMenuOpened(menuId) {
        return (state.isRendered && state.alwaysOpenedMenus.includes(menuId)) || state.openedMenus.includes(menuId);
      },

      menuStyleFor(menuId, $el, _forceRenderCounter) {
        if (!state.isRendered) {
          return;
        }

        const clientRect = $el.getBoundingClientRect();
        const maxHeight = window.innerHeight - clientRect.top;
        const openedCollapsedMenu = this.isOpenedCollapsedMenu(menuId);
        // Explicit, not false: Alpine reverts to the `auto` set below, so a falsy height never clears it.
        let height = '0px';

        if (!state.collapsed && this.isMenuOpened(menuId)) {
          $el.style.height = 'auto';
          height = $el.scrollHeight + 'px';
        }

        const style = {
          height,
          maxHeight: openedCollapsedMenu ? maxHeight + 'px' : 'auto',
        };

        if (openedCollapsedMenu) {
          style.width = '220px';
          style.maxWidth = '220px';
          style.minWidth = '220px';
        }

        // Show the menu items at the bottom of the menu
        if (maxHeight < 100 && openedCollapsedMenu) {
          style.position = 'absolute';
          style.top = 'auto';
          style.bottom = 0;
          style.maxHeight = clientRect.top + 'px';
        }

        return style;
      },

      saveScrollPosition() {
        state.scrollPosition = this.$refs.menusScroller.scrollTop;
      },

      restoreScrollPosition() {
        setTimeout(() => {
          if (this.$refs.menusScroller.scrollTop === 0) {
            // Only if the scroll was auto moved to top
            this.$refs.menusScroller.scrollTop = state.scrollPosition;
          }
        }, 0.1);
      },

      isAlwaysOpen(menuId) {
        return state.alwaysOpenedMenus.includes(menuId);
      },

      isFavoriteItem(itemId) {
        return state.favoriteMenus.includes(itemId);
      },

      toggleMobileSidebar() {
        state.visible = !state.visible;
        state.collapsed = false;
      },

      toggleMenu(menuId) {
        state.slideAnimationEnabled = true;
        if (this.isMenuOpened(menuId)) {
          state.openedMenus = state.openedMenus.filter((id) => id !== menuId);
        } else {
          state.openedMenus = [...state.openedMenus, menuId];
        }
        this.disableAnimation();
      },

      pendingActionCountsEnabled() {
        return document.body?.dataset?.batchManualTimeEntriesTimesheetsEnabled === 'true';
      },

      pendingActionCountsUrl() {
        if (!this.pendingActionCountsEnabled()) {
          return null;
        }

        const organizationId = document.body?.dataset?.organizationId;

        if (!organizationId) {
          return null;
        }

        const url = new URL(`/organizations/${organizationId}/pending_action_counts`, window.location.origin);

        url.searchParams.append('scopes[]', PENDING_ACTION_SCOPE);

        return url.toString();
      },

      async loadPendingActionCounts() {
        const requestToken = (this.currentPendingActionRequestToken || 0) + 1;

        this.currentPendingActionRequestToken = requestToken;

        if (!this.pendingActionCountsEnabled()) {
          return;
        }

        const pendingActionTargets = document.querySelector('[data-pending-action-key]');

        if (!pendingActionTargets) {
          return;
        }

        const url = this.pendingActionCountsUrl();

        if (!url) {
          return;
        }

        try {
          const response = await fetch(url, {
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error(`Failed to load pending action counts (${response.status})`);
          }

          const payload = await response.json();

          if (requestToken !== this.currentPendingActionRequestToken) {
            return;
          }

          this.updatePendingActionBadges(payload?.scopes?.[PENDING_ACTION_SCOPE] || {});
        } catch (error) {
          console.warn('Error loading pending action counts:', error);
        }
      },

      updatePendingActionBadges(menuScopes) {
        PENDING_ACTION_KEYS.forEach((key) => {
          this.updatePendingActionBadge(key, menuScopes[key]);
        });
      },

      updatePendingActionBadge(key, badge) {
        const badgeElements = document.querySelectorAll(`[data-pending-action-key="${key}"]`);

        if (!badgeElements.length) {
          return;
        }

        const count = Number(badge?.value || 0);
        const formattedCount = pendingActionCountLabel(count);
        const tooltipText = this.pendingActionTooltip(key, badge?.sources || {});

        badgeElements.forEach((badgeElement) => {
          badgeElement.style.display = count === 0 ? 'none' : '';

          const numberElement = badgeElement.querySelector('.number');

          if (numberElement) {
            numberElement.textContent = formattedCount;
          }

          const badgeState = badgeElement._x_dataStack?.[0];

          if (badgeState) {
            badgeState.pendingActionTooltip = tooltipText;
          }

          const linkScope = badgeElement.closest('[data-pending-action-default-href]');

          if (linkScope) {
            const menuLink = linkScope.matches('a[data-pending-action-default-href]') ?
              linkScope :
              linkScope.querySelector('a[role="menuitem"]');
            const defaultHref = linkScope.dataset.pendingActionDefaultHref;
            const pendingActionHref = linkScope.dataset.pendingActionHref;
            const hrefSource = linkScope.dataset.pendingActionHrefSource;
            const hrefCount = hrefSource ? Number(badge?.sources?.[hrefSource] || 0) : count;

            if (menuLink && defaultHref && pendingActionHref) {
              menuLink.setAttribute('href', hrefCount > 0 ? pendingActionHref : defaultHref);
            }
          }
        });
      },

      pendingActionTooltip(key, sources) {
        const reviewTimesheets = Number(sources.timesheets_review || 0);
        const submitTimesheets = Number(sources.timesheets_submit || 0);
        const reviewManualTime = Number(sources.manual_time_review || 0);
        const items = [];

        if (['timesheets', 'edit_approve', 'view_edit'].includes(key)) {
          if (reviewTimesheets > 0) {
            items.push(`Review ${reviewTimesheets} ${pluralize(reviewTimesheets, 'timesheet')}`);
          }

          if (submitTimesheets > 0) {
            items.push(`Submit ${submitTimesheets} ${pluralize(submitTimesheets, 'timesheet')}`);
          }
        }

        if (['timesheets', 'manual_time_requests', 'approvals'].includes(key) && reviewManualTime > 0) {
          items.push(`Review ${reviewManualTime} ${pluralize(reviewManualTime, 'manual time request')}`);
        }

        return items.join(key === 'timesheets' ? '\n' : '. ');
      },
    }
  });
}
